import { ChevronDown, ChevronTop, WarningCircle } from 'assets/icons';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import JustificatifConfirmation from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import PillTabs from 'components/tabs/PillTabs';
import {
  BONUS_VALUE_TYPES,
  bonusValueTypeFromCoefficient,
  formatBonusValue,
} from 'helper/bonusValue';
import UseContext from 'hooks/useContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from '../../../../../server';

const STEPS = {
  BONUS: 'bonus',
  UNIT: 'unit',
  CONFIRMATION: 'confirmation',
  // JBWEB-1214: a prime reaches every contract the company signs, so the line
  // is backed by the client's written request for it before it is written.
  JUSTIFICATIF: 'justificatif',
};
const STEP_ORDER = [
  STEPS.BONUS,
  STEPS.UNIT,
  STEPS.CONFIRMATION,
  STEPS.JUSTIFICATIF,
];

/**
 * `nature` comes straight from BeSTT and is what separates the two kinds of
 * line: a « Soumis » element is billed at the mission rate — a prime — while a
 * « Non Soumis » one is billed at 1 — an indemnité. The rubric label cannot be
 * used instead: some are plainly misnamed (« INDEMNITE PANIER SOUMIS »), and
 * `nature` is the field that decides how the line is actually charged.
 *
 * Anything else (« Calcul » aggregates, employer charges with no nature) is a
 * payroll line that has no business in this picker.
 */
const NATURE_PRIME = 'Soumis';
const NATURE_ALLOWANCE = 'Non Soumis';
const SELECTABLE_NATURES = [NATURE_PRIME, NATURE_ALLOWANCE];

/**
 * What may be added here as a free line, read off the referential coefficient
 * rather than off a list of labels:
 *
 * - `> 1` is a whole pay rate, not a majoration — the « HEURES SUPP… » family,
 *   configured on its own screens. A label list used to hold these back and let
 *   « Heures supplémentaires de nuit 25% » through on a spelling difference.
 * - `<= 0` is a retenue or a state aid (« RETENUE TROP PERCU » at -1, `SATD`
 *   at 0). Those are billed at neither the mission rate nor 1, and forcing a
 *   coefficient of 1 on a retenue would flip its sign on the invoice.
 */
const isSelectableCoefficient = (coefficient) =>
  coefficient > 0 && coefficient <= 1;

/**
 * Meal vouchers have their own card on the company sheet, where the unit value
 * of a ticket can be entered — a field this form does not have. Matched on the
 * BeSTT code rather than the label, which carries a trailing space on both.
 */
const RESTAURANT_TICKET_CODES = [755, 763];

/** Section order of the unit picker; anything unknown is appended after. */
const UNIT_GROUP_ORDER = [
  'Journées',
  'Heures',
  'Exemples de formules',
  'Exemples de tranches',
  'Autres',
];

/** Chips shown under the unit field; more would crowd the modal footer. */
const MAX_UNIT_SUGGESTIONS = 3;

/** How often a rubric has been used, all units taken together. */
const timesUsed = (bonus) =>
  (bonus.unit_usage ?? []).reduce(
    (total, entry) => total + (entry.count ?? 0),
    0,
  );

/**
 * Most used first, then alphabetical. The counters make the everyday rubrics
 * reachable without scrolling, while the ones nobody has used yet — the large
 * majority — stay in an order the eye can search.
 *
 * Names are trimmed and compared without case or accents: the referential
 * holds « PRIME OUTILLAGE » next to « Prime parisienne », and a raw sort would
 * file them in separate blocks.
 */
const byPopularityThenName = (a, b) =>
  timesUsed(b) - timesUsed(a) ||
  (a.name ?? '')
    .trim()
    .localeCompare((b.name ?? '').trim(), 'fr', { sensitivity: 'base' });

const isPrime = (bonus) => bonus?.nature === NATURE_PRIME;

/**
 * The referential coefficient is a pay multiplier whose meaning depends on the
 * rubric family: on a « MAJ… » line it carries the majoration alone (0.3 for
 * +30 %), while on an « HEURES SUPP… » line it carries the whole rate (1.25 for
 * +25 %). Only the first reading is a majoration, so only a coefficient
 * strictly between 0 and 1 prefills the field — a coefficient of 1 means « no
 * majoration » on 188 of the 228 selectable rubrics, and offering 100 % there
 * would be wrong far more often than right.
 */
const defaultMajoration = (bonus) => {
  const coefficient = bonus?.coefficient;
  if (!(coefficient > 0 && coefficient < 1)) return '';
  return Math.round(coefficient * 10000) / 100;
};

const VALUE_TYPE_OPTIONS = [
  { type: BONUS_VALUE_TYPES.PERCENTAGE, symbol: '%' },
  { type: BONUS_VALUE_TYPES.AMOUNT, symbol: '€' },
];

const BonusValueTypeToggle = ({ value, onChange, label, nameOf }) => (
  <div
    role={'group'}
    aria-label={label}
    className={
      'inline-flex gap-px rounded-md border border-gray-200 bg-gray-50 p-px'
    }
  >
    {VALUE_TYPE_OPTIONS.map(({ type, symbol }) => {
      const isActive = value === type;
      return (
        <button
          key={type}
          type={'button'}
          aria-pressed={isActive}
          aria-label={nameOf(type)}
          onClick={() => onChange(type)}
          className={`min-w-[32px] px-2 rounded border text-sm leading-5 transition-colors ${
            isActive
              ? 'border-yellow-500 bg-yellow-50 text-yellow-700 font-medium'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {symbol}
        </button>
      );
    })}
  </div>
);

const UnitRow = ({ unit, isSelected, onSelect }) => (
  <button
    type={'button'}
    data-unit-id={unit._id}
    onClick={() => onSelect(unit)}
    className={`w-full flex items-center gap-4 px-4 py-3 text-left border-b border-gray-100 transition-colors ${
      isSelected
        ? 'border border-yellow-500 bg-yellow-50'
        : 'bg-white hover:bg-gray-50'
    }`}
  >
    <span className={'flex-1 min-w-0 truncate text-gray-700'}>
      {unit.description || unit.name}
    </span>
    <Badge type={'SECONDARY'}>{unit.name}</Badge>
  </button>
);

const UnitGroup = ({
  group,
  units,
  isCollapsed,
  onToggle,
  selectedUnitId,
  onSelect,
}) => (
  <div>
    <button
      type={'button'}
      onClick={onToggle}
      className={
        'w-full flex items-center gap-1 px-4 py-2.5 bg-gray-50 text-gray-900 uppercase'
      }
    >
      <span className={'flex-1 text-left truncate'}>{group}</span>
      {isCollapsed ? (
        <ChevronDown wh={20} color={'#374151'} />
      ) : (
        <ChevronTop wh={20} color={'#374151'} />
      )}
    </button>
    {!isCollapsed &&
      units.map((unit) => (
        <UnitRow
          key={unit._id}
          unit={unit}
          isSelected={unit._id === selectedUnitId}
          onSelect={onSelect}
        />
      ))}
  </div>
);

// The separator belongs between rows, so the last one drops it rather than
// underlining the bottom of the recap.
const RecapRow = ({ label, children }) => (
  <div
    className={
      'flex items-center gap-3 py-3.5 pl-3 pr-4 border-b border-gray-100 last:border-b-0'
    }
  >
    <span className={'flex-1 min-w-0 truncate text-gray-900'}>{label}</span>
    <span className={'text-right text-gray-700 truncate'}>{children}</span>
  </div>
);

const ModalEditBonusesAndAllowances = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn, language } = UseContext();
  const text = (key) => language.getValue(`bonusValue.${key}`);
  const token = localStorage.getItem('xsrfToken');
  const isEdition = !!modalData.idBonusesAndAllowances;
  // The page's changes held back (JBWEB-1468): this line joins them at its
  // confirmation, without a justificatif step of its own.
  const draft = modalData.draft;
  const draftKey = isEdition ? `bonus:${modalData.idBonusesAndAllowances}` : `bonus:new:${Date.now()}`;
  const steps = draft ? STEP_ORDER.filter((one) => one !== STEPS.JUSTIFICATIF) : STEP_ORDER;
  // The line as it was, to show what an edit replaces.
  const loadedLine = useRef(null);

  const [step, setStep] = useState(STEPS.BONUS);
  const justificatif = useJustificatifConfirmation();
  const [isWaiting, setIsWaiting] = useState(false);
  const [isWaitingBonusList, setIsWaitingBonusList] = useState(false);
  const [isWaitingBonusUnitList, setIsWaitingBonusUnitList] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bonusList, setBonusList] = useState([]);
  const [bonusUnitList, setBonusUnitList] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState([]);
  // The code typed or picked in the footer field is what drives the unit step:
  // the list above filters on it, and the step only validates once it names a
  // unit of the referential.
  const [unitSearch, setUnitSearch] = useState('');
  const unitListRef = useRef(null);
  const [bonusesAndAllowances, setBonusesAndAllowances] = useState({
    name: '',
    unit: '',
    payed: '',
    coef: '',
    payed_type: undefined,
  });
  const [errors, setErrors] = useState({ name: '', payed: '', unit: '' });

  // Rubrics the company already carries, handed over by the sheet that opens
  // this modal. Only used to warn: the same rubric with two different units is
  // a legitimate setup, and only the person configuring it can tell.
  const configuredBonusNames = modalData.configuredBonusNames ?? [];

  const selectedBonus = bonusList.find(
    (bonus) => bonus._id === bonusesAndAllowances.name,
  );
  // A line saved before the type existed has none: it reads as the api reads
  // it, from its rubric — which is why the form waits for the rubric list.
  const payedType =
    bonusesAndAllowances.payed_type ??
    bonusValueTypeFromCoefficient(selectedBonus?.coefficient);
  // What the rubric is most often paired with, most frequent first. The
  // counters come from production contracts and grow with every save here, so
  // the order follows real practice. Never a default: the unit belongs to the
  // agreement with the company, so the same rubric legitimately differs from
  // one to the next — a rubric nobody has used yet simply offers nothing.
  const alreadyConfigured =
    !isEdition &&
    !!selectedBonus &&
    configuredBonusNames.some(
      (name) => name?.trim() === selectedBonus.name?.trim(),
    );

  const suggestedUnits = [...(selectedBonus?.unit_usage ?? [])]
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .map((entry) => entry.unit)
    .filter((code) => bonusUnitList.some((unit) => unit.name === code))
    .slice(0, MAX_UNIT_SUGGESTIONS)
    .map((code) => ({ name: code }));

  const matchedUnit = useMemo(() => {
    const search = unitSearch.trim().toLowerCase();
    if (!search) return null;
    return bonusUnitList.find((unit) => unit.name?.toLowerCase() === search);
  }, [unitSearch, bonusUnitList]);

  const groupedUnits = useMemo(() => {
    const search = unitSearch.trim().toLowerCase();
    // Filtering narrows the list while the code is being typed, but stops
    // once it names an existing unit: the choice is then marked by the
    // highlight, and the whole list stays browsable so another can be picked
    // without clearing the field first.
    const matches = (unit) =>
      matchedUnit ||
      !search ||
      unit.name?.toLowerCase().includes(search) ||
      unit.description?.toLowerCase().includes(search);

    const groups = new Map();
    bonusUnitList.filter(matches).forEach((unit) => {
      const group = unit.group || 'Autres';
      groups.set(group, [...(groups.get(group) ?? []), unit]);
    });

    return [...groups.entries()].sort(([a], [b]) => {
      const rankA = UNIT_GROUP_ORDER.indexOf(a);
      const rankB = UNIT_GROUP_ORDER.indexOf(b);
      if (rankA === -1 && rankB === -1) return a.localeCompare(b);
      if (rankA === -1) return 1;
      if (rankB === -1) return -1;
      return rankA - rankB;
    });
  }, [bonusUnitList, unitSearch, matchedUnit]);

  useEffect(() => {
    if (!modalData.idCompany) return;

    setIsWaitingBonusList(true);
    ApiDatabase.getBonusListCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setBonusList(
          data.bonusList
            .filter(
              (bonus) =>
                SELECTABLE_NATURES.includes(bonus.nature) &&
                isSelectableCoefficient(bonus.coefficient) &&
                !RESTAURANT_TICKET_CODES.includes(bonus.id_bestt),
            )
            .sort(byPopularityThenName),
        );
        setIsWaitingBonusList(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaitingBonusList(false);
        }
      },
    );

    setIsWaitingBonusUnitList(true);
    ApiDatabase.getBonusUnitListCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setBonusUnitList(data.bonusUnitList);
        setIsWaitingBonusUnitList(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaitingBonusUnitList(false);
        }
      },
    );
  }, [modalData.idCompany]);

  useEffect(() => {
    if (!modalData.idCompany || !modalData.idBonusesAndAllowances) return;

    setIsWaiting(true);
    ApiDatabase.getBonusAndAllowance(
      {
        token,
        idCompany: modalData.idCompany,
        idBonusesAndAllowances: modalData.idBonusesAndAllowances,
      },
      (data) => {
        if (data.bonusAndAllowance) {
          loadedLine.current = data.bonusAndAllowance;
          setBonusesAndAllowances(draft?.pending(draftKey) ?? data.bonusAndAllowance);
        }
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaiting(false);
        }
      },
    );
  }, [modalData.idCompany, modalData.idBonusesAndAllowances]);

  // On edition the unit arrives as an id; the footer field shows codes, so it
  // is seeded once the referential is there to resolve it.
  useEffect(() => {
    if (unitSearch || !bonusesAndAllowances.unit) return;
    const unit = bonusUnitList.find(
      (item) => item._id === bonusesAndAllowances.unit,
    );
    if (unit) setUnitSearch(unit.name);
  }, [bonusUnitList, bonusesAndAllowances.unit]);

  const isBonusStepValid = () => {
    const newErrors = {
      name: bonusesAndAllowances.name ? '' : 'Veuillez sélectionner une prime',
      payed:
        bonusesAndAllowances.payed === '' ||
        bonusesAndAllowances.payed === null ||
        bonusesAndAllowances.payed <= 0
          ? text(`required.${payedType}`)
          : '',
    };
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !newErrors.name && !newErrors.payed;
  };

  const isUnitStepValid = () => {
    const error = matchedUnit
      ? ''
      : 'Veuillez choisir une unité de la liste ou saisir un code existant';
    setErrors((prev) => ({ ...prev, unit: error }));
    return !error;
  };

  const goNext = () => {
    if (step === STEPS.BONUS) {
      if (!isBonusStepValid()) return;
      return setStep(STEPS.UNIT);
    }
    if (step === STEPS.UNIT) {
      if (!isUnitStepValid()) return;
      setBonusesAndAllowances((prev) => ({ ...prev, unit: matchedUnit._id }));
      return setStep(STEPS.CONFIRMATION);
    }
    if (step === STEPS.CONFIRMATION && draft) return stageInDraft();
    if (step === STEPS.CONFIRMATION) setStep(STEPS.JUSTIFICATIF);
  };

  // The figure is cleared with the type: kept, a 5 % would silently become 5 €.
  const changePayedType = (type) => {
    if (type === payedType) return;
    setBonusesAndAllowances((prev) => ({
      ...prev,
      payed_type: type,
      payed: '',
    }));
    setErrors((prev) => ({ ...prev, payed: '' }));
  };

  const goBack = () =>
    setStep(steps[Math.max(0, steps.indexOf(step) - 1)]);

  const selectUnit = (unit) => {
    setUnitSearch(unit.name);
    setErrors((prev) => ({ ...prev, unit: '' }));
    // A unit picked from a suggestion may sit in a section the user has
    // folded; scrolling to a hidden row would land on nothing.
    if (unit.group) {
      setCollapsedGroups((prev) =>
        prev.filter((group) => group !== unit.group),
      );
    }
  };

  // Bring the matching row into view — on selection, and on arriving at the
  // step with a unit already chosen. `step` belongs in the dependencies for
  // that second case: on edition the unit is known long before the list is
  // rendered, and the container is still null when the match is first set.
  useEffect(() => {
    if (step !== STEPS.UNIT || !matchedUnit) return;
    unitListRef.current
      ?.querySelector(`[data-unit-id="${matchedUnit._id}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [matchedUnit, step]);

  const toggleGroup = (group) =>
    setCollapsedGroups((prev) =>
      prev.includes(group)
        ? prev.filter((item) => item !== group)
        : [...prev, group],
    );

  const handleSubmit = () => {
    if (!modalData.idCompany || !selectedBonus || !matchedUnit) return;
    justificatif.confirm(save);
  };

  // A prime follows the mission rate, so its coefficient is left unset and
  // resolved at contract time; an indemnité is always billed at 1.
  const linePayload = () => ({
    ...bonusesAndAllowances,
    payed_type: payedType,
    unit: matchedUnit._id,
    coef: isPrime(selectedBonus) ? null : 1,
  });

  // The save, as the modal return it resolves with: run here, or later with
  // the page's other changes.
  const send = (storedJustificatif) => {
    const payload = linePayload();
    return new Promise((resolve, reject) => {
      const resolveAs = (type) => (data) => resolve({ type, bonusesAndAllowances: data.bonus });
      if (isEdition) {
        ApiDatabase.postUpdBonusesAndAllowances(
          {
            token,
            idCompany: modalData.idCompany,
            idBonusesAndAllowances: modalData.idBonusesAndAllowances,
            bonusesAndAllowances: payload,
            justificatif: storedJustificatif,
          },
          resolveAs('bonuses_and_allowances_updated'),
          reject,
        );
      } else {
        ApiDatabase.postAddBonusesAndAllowances(
          {
            token,
            idCompany: modalData.idCompany,
            bonusesAndAllowances: payload,
            justificatif: storedJustificatif,
          },
          resolveAs('bonuses_and_allowances_added'),
          reject,
        );
      }
    });
  };

  // No preview: the api returns the line with its rubric and unit resolved.
  const stageInDraft = () => {
    if (!modalData.idCompany || !selectedBonus || !matchedUnit) return;
    const lineLabel = (line) =>
      line ? [formatBonusValue({ ...line, payed_type: line.payed_type ?? payedType }), line.unitName].filter(Boolean).join(', ') : null;
    draft.stage({
      key: draftKey,
      section: draftKey,
      label: `${isEdition ? 'Prime' : 'Nouvelle prime'} : ${selectedBonus.name}`,
      before: isEdition ? lineLabel(loadedLine.current) : null,
      after: lineLabel({ ...linePayload(), unitName: matchedUnit.name }),
      form: bonusesAndAllowances,
      commit: send,
    });
    toggle();
  };

  const save = (storedJustificatif) => {
    setIsLoading(true);
    send(storedJustificatif).then(
      (saved) => {
        setIsLoading(false);
        toast.success(isEdition ? 'Les primes ont été mises à jour' : 'Les primes ont été ajoutées');
        setModalDataReturn(saved);
        toggle();
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsLoading(false);
        }
      },
    );
  };

  const isWaitingAnything =
    isWaiting || isWaitingBonusList || isWaitingBonusUnitList;

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {isEdition
          ? 'Modifier une prime & indemnité'
          : 'Ajouter une prime & indemnité'}
      </Modalheader>
      <div className={'px-6 py-3'}>
        <PillTabs
          // Steps can only be revisited backwards: jumping ahead would skip the
          // validation each step owns. A step still ahead is therefore not
          // reachable, and says so on hover rather than looking clickable —
          // which is the same thing as the card's rules, since selecting the
          // prime and then the unit is exactly what moves the step forward.
          tabs={[
            { id: STEPS.BONUS, label: 'Prime' },
            { id: STEPS.UNIT, label: 'Unités' },
            { id: STEPS.CONFIRMATION, label: 'Confirmation' },
            { id: STEPS.JUSTIFICATIF, label: 'Justificatif' },
          ]
            .filter((tab) => steps.includes(tab.id))
            .map((tab) => ({
              ...tab,
              disabled: steps.indexOf(tab.id) > steps.indexOf(step),
            }))}
          activeTab={step}
          onChange={(tabId) => {
            if (steps.indexOf(tabId) < steps.indexOf(step)) {
              setStep(tabId);
            }
          }}
        />
      </div>
      <ModalBody>
        {isWaitingAnything ? (
          <div className="flex flex-col gap-2.5 text-sm">
            <div className={'h-16 skeleton-bar w-full'}></div>
            <div className={'h-16 skeleton-bar w-full'}></div>
            <div className={'h-16 skeleton-bar w-full'}></div>
          </div>
        ) : (
          <>
            {step === STEPS.BONUS && (
              <div className="flex flex-col gap-2.5 text-sm motion-safe:animate-contentIn">
                <StyledInput
                  type="selectMultipleRadio"
                  label="Sélectionner une prime"
                  value={selectedBonus?.name || '-'}
                  list={bonusList.map((bonus) => ({
                    ...bonus,
                    value: bonus._id,
                    label: bonus.name,
                    checked: bonus._id === bonusesAndAllowances.name,
                  }))}
                  onChange={(updatedList) => {
                    const checked = updatedList.find((item) => item.checked);
                    // Changing the rubric restarts the line: the type and the
                    // majoration follow the new one's own rate rather than
                    // keeping what belonged to the previous choice.
                    const type = bonusValueTypeFromCoefficient(
                      checked?.coefficient,
                    );
                    setBonusesAndAllowances((prev) => ({
                      ...prev,
                      name: checked?.value || '',
                      payed_type: type,
                      payed:
                        type === BONUS_VALUE_TYPES.PERCENTAGE
                          ? defaultMajoration(checked)
                          : '',
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      name: checked?.value
                        ? ''
                        : 'Veuillez sélectionner une prime',
                      payed: '',
                    }));
                  }}
                  isSearchable
                  error={errors.name}
                  dropdownFullWidth
                />
                {alreadyConfigured && (
                  <div className={'flex items-center gap-1 text-yellow-600'}>
                    <WarningCircle wh={16} color={'#CA8A04'} />
                    Cette prime est déjà configurée pour cette entreprise.
                  </div>
                )}
                {/* The toggle sits over the label row but outside StyledInput's
                    <label>: inside it, the first button would become the
                    labelled control instead of the input. */}
                <div className={'relative'}>
                  <div className={'absolute right-0 top-0 z-[1]'}>
                    <BonusValueTypeToggle
                      value={payedType}
                      onChange={changePayedType}
                      label={text('typeLabel')}
                      nameOf={(type) => text(`types.${type}`)}
                    />
                  </div>
                  <StyledInput
                    label={text(`fieldLabel.${payedType}`)}
                    type={'number'}
                    unit={
                      payedType === BONUS_VALUE_TYPES.PERCENTAGE ? '%' : '€'
                    }
                    step="0.01"
                    value={bonusesAndAllowances.payed}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBonusesAndAllowances((prev) => ({
                        ...prev,
                        payed: value !== '' ? parseFloat(value) : '',
                      }));
                    }}
                    hint={text(`hint.${payedType}`)}
                    error={errors.payed}
                  />
                </div>
              </div>
            )}

            {step === STEPS.UNIT && (
              <div className={'flex flex-col gap-2 text-sm motion-safe:animate-contentIn'}>
                {/* The modal's own height is unbounded, so the list is what
                    scrolls — otherwise five sections push the footer off
                    screen. overflow-x stays hidden to keep the rounded
                    corners clipping the rows. */}
                <div
                  ref={unitListRef}
                  className={
                    'border border-gray-100 rounded-md overflow-x-hidden overflow-y-auto max-h-[50vh]'
                  }
                >
                  {groupedUnits.length === 0 ? (
                    <div className={'px-4 py-6 text-center text-gray-500'}>
                      Aucune unité ne correspond à « {unitSearch} »
                    </div>
                  ) : (
                    groupedUnits.map(([group, units]) => (
                      <UnitGroup
                        key={group}
                        group={group}
                        units={units}
                        isCollapsed={collapsedGroups.includes(group)}
                        onToggle={() => toggleGroup(group)}
                        selectedUnitId={matchedUnit?._id}
                        onSelect={selectUnit}
                      />
                    ))
                  )}
                </div>
                {errors.unit && (
                  <div className={'text-red-500'}>{errors.unit}</div>
                )}
              </div>
            )}

            {step === STEPS.CONFIRMATION && (
              <div className={'flex flex-col text-sm motion-safe:animate-contentIn'}>
                <RecapRow label={'Type'}>
                  {isPrime(selectedBonus) ? 'Prime' : 'Indemnité'}
                </RecapRow>
                <RecapRow label={text(`fieldLabel.${payedType}`)}>
                  {formatBonusValue({
                    payed: bonusesAndAllowances.payed,
                    payed_type: payedType,
                  })}
                </RecapRow>
                <RecapRow label={'Unité de calcul'}>
                  {matchedUnit?.description} ({matchedUnit?.name})
                </RecapRow>
                <RecapRow label={'Coefficient'}>
                  {isPrime(selectedBonus)
                    ? 'Variable selon la mission'
                    : '1.00'}
                </RecapRow>
                <RecapRow label={'Facturé (HT)'}>
                  Calculé selon la mission
                </RecapRow>
              </div>
            )}
          </>
        )}
            {step === STEPS.JUSTIFICATIF && (
              <div className="motion-safe:animate-contentIn">
                <JustificatifConfirmation
                  file={justificatif.file}
                  setFile={justificatif.setFile}
                  isAttested={justificatif.isAttested}
                  setIsAttested={justificatif.setIsAttested}
                  error={justificatif.error}
                  description={
                    'Cette prime est reportée sur les contrats de la mission. Un justificatif est requis avant validation.'
                  }
                />
              </div>
            )}
      </ModalBody>
      <ModalFooter>
        {step === STEPS.UNIT && (
          <div className={'mr-auto w-[280px] shrink'}>
            {/* `mr-auto` rather than `flex-1`: the footer is justify-end, and
                a growing child would leave no free space for the margin to
                absorb, parking the field beside the buttons instead of on the
                left. */}
            <StyledInput
              type={'text'}
              placeholder={'Unité'}
              value={unitSearch}
              onChange={(e) => setUnitSearch(e.target.value)}
              listSuggestions={suggestedUnits}
              // A chip only carries a code; the referential entry is what
              // knows which section holds it.
              onClickSuggestion={(suggestion) =>
                selectUnit(
                  bonusUnitList.find((unit) => unit.name === suggestion.name) ??
                    suggestion,
                )
              }
            />
          </div>
        )}
        {step === STEPS.BONUS ? (
          <Button size={'LG'} color={'SECONDARY'} onClick={toggle}>
            Annuler
          </Button>
        ) : (
          <Button size={'LG'} color={'SECONDARY'} onClick={goBack}>
            Retour
          </Button>
        )}
        {step === STEPS.JUSTIFICATIF ? (
          <Button
            size={'LG'}
            color={
              isLoading || justificatif.isUploading || !justificatif.canConfirm
                ? 'DISABLED'
                : 'PRIMARY'
            }
            onClick={
              isLoading || justificatif.isUploading ? undefined : handleSubmit
            }
          >
            {isLoading || justificatif.isUploading
              ? 'Confirmation ...'
              : 'Confirmer'}
          </Button>
        ) : (
          <Button size={'LG'} color={'PRIMARY'} onClick={goNext}>
            {draft && step === STEPS.CONFIRMATION ? 'Ajouter aux modifications' : 'Suivant'}
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalEditBonusesAndAllowances;
